import { tumSubeler } from "../domain/context.js";
import { yapabilir } from "../domain/auth.js";
import { PHOTO_KEY, ESKI_PHOTO_KEY, PHOTO_MAX_BYTES, PHOTO_SIZE, firebaseHazir } from "../config.js";
import { avatarMarkup } from "./avatar.js";
import { esc, fmtDate } from "./fmt.js";
import { state } from "../state.js";

// ============================================================
// FOTOĞRAF YÜKLEME
//
// Seçilen görsel tarayıcıda kare kırpılıp 256×256'ya küçültülür,
// sonra depoya yazılır. İKİ depo var ve ayrımı .env belirliyor:
//
//   gerçek mod → Firebase Storage. Kişi kaydına yalnızca YOL
//                (photoPath) yazılır; görüntülenebilir URL açılışta
//                çözülür ve bellekte kalır. Gerekçesi
//                lib/storage-dosyalar.js başında yazılı.
//   demo mod   → localStorage. Satış sunumu Firebase'siz çalışsın.
//
// Gerçek modda kayıt repo katmanından geçiyor: hem Firestore
// belgesine yazılıyor (repo/projeksiyon.js) hem denetim izine
// düşüyor. Çocuk fotoğrafının ne zaman, kim tarafından eklendiği
// ve silindiği KVKK açısından izlenebilir olmalı.
// ============================================================
export const PhotoStore = {
  read() {
    try {
      const ham = localStorage.getItem(PHOTO_KEY) ?? this.eskidenTasi();
      return JSON.parse(ham) ?? {};
    } catch { return {}; }
  },

  // Ad değişikliğinden önce kaydedilmiş fotoğraflar kaybolmasın diye
  // eski anahtar bir kereye mahsus yeni anahtara kopyalanır.
  eskidenTasi() {
    try {
      const eski = localStorage.getItem(ESKI_PHOTO_KEY);
      if (!eski) return null;
      localStorage.setItem(PHOTO_KEY, eski);
      localStorage.removeItem(ESKI_PHOTO_KEY);
      return eski;
    } catch { return null; }
  },
  write(all) {
    try { localStorage.setItem(PHOTO_KEY, JSON.stringify(all)); return null; }
    catch { return "Tarayıcı depolama alanı dolu — fotoğraf yalnızca bu oturumda görünecek."; }
  },

  /**
   * Açılışta fotoğrafları veri katmanına bindirir.
   *
   * Gerçek modda kişi kaydında photoPath duruyor; buradaki iş onu
   * görüntülenebilir URL'e çevirmek. Tek tek beklemek yerine hepsi
   * paralel çözülüyor, ve bir tanesinin reddedilmesi diğerlerini
   * düşürmüyor — yetkisi olmayan kişi avatarı baş harflerle görür.
   */
  async hydrate() {
    if (!firebaseHazir) {
      const all = this.read();
      tumSubeler().forEach(t => {
        const kayit = all[t.id] ?? {};
        [...t.students, ...t.staff].forEach(p => {
          const e = kayit[p.id];
          if (e) { p.photo = e.dataUrl; p.photoConsentAt = e.consentAt ?? null; }
        });
      });
      return;
    }

    const { fotografUrl } = await import("./storage-dosyalar.js");
    const bekleyen = tumSubeler()
      .flatMap(t => [...t.students, ...t.staff])
      .filter(p => p.photoPath);

    await Promise.all(bekleyen.map(async p => {
      p.photo = await fotografUrl(p.photoPath);
    }));
  },

  /**
   * @returns {Promise<string|null>} kullanıcıya gösterilecek uyarı
   */
  async save(branchId, person, dataUrl, consentAt) {
    if (!firebaseHazir) {
      const all = this.read();
      (all[branchId] ??= {})[person.id] = { dataUrl, consentAt };
      const uyari = this.write(all);
      person.photo = dataUrl;
      person.photoConsentAt = consentAt;
      return uyari;
    }

    const { fotografYukle } = await import("./storage-dosyalar.js");
    const { Fotograf } = await import("../repo/entities.js");

    try {
      const yol = await fotografYukle(state.tenantId, branchId, person.id, dataUrl);
      // Önizleme hemen görünsün diye data URL'i bellekte tutuyoruz;
      // bir sonraki açılışta Storage'dan çözülecek.
      Fotograf.ayarla(branchId, person, { photoPath: yol, photoConsentAt: consentAt });
      person.photo = dataUrl;
      return null;
    } catch (e) {
      return e?.code === "storage/unauthorized"
        ? "Fotoğraf yükleme yetkiniz yok."
        : `Fotoğraf yüklenemedi: ${e?.message ?? "bilinmeyen hata"}`;
    }
  },

  async remove(branchId, person) {
    if (!firebaseHazir) {
      const all = this.read();
      if (all[branchId]) { delete all[branchId][person.id]; this.write(all); }
      person.photo = null;
      person.photoConsentAt = null;
      return null;
    }

    const { fotografSil } = await import("./storage-dosyalar.js");
    const { Fotograf } = await import("../repo/entities.js");

    try {
      // Önce dosya, sonra kayıt. Ters sırada olsaydı kayıt silinip
      // dosya Storage'da öksüz kalabilirdi — KVKK "sil" talebinde
      // gerçekten silinmiş olması gerekiyor.
      await fotografSil(person.photoPath);
      Fotograf.ayarla(branchId, person, { photoPath: null, photoConsentAt: null });
      person.photo = null;
      return null;
    } catch (e) {
      return `Fotoğraf silinemedi: ${e?.message ?? "bilinmeyen hata"}`;
    }
  },
};

export function pickImage() {
  return new Promise(resolve => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.addEventListener("change", () => resolve(input.files?.[0] ?? null), { once: true });
    input.click();
  });
}

// Görseli merkezden kare kırpar, PHOTO_SIZE'a küçültür, JPEG data URL döner.
export function fileToSquareJPEG(file, size = PHOTO_SIZE) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) return reject(new Error("Yalnızca görsel dosyası yüklenebilir."));
    if (file.size > PHOTO_MAX_BYTES) return reject(new Error("Dosya çok büyük — en fazla 8 MB."));

    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const kenar = Math.min(img.naturalWidth, img.naturalHeight);
      if (!kenar) return reject(new Error("Görsel okunamadı."));
      const canvas = document.createElement("canvas");
      canvas.width = canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, (img.naturalWidth - kenar) / 2, (img.naturalHeight - kenar) / 2, kenar, kenar, 0, 0, size, size);
      try { resolve(canvas.toDataURL("image/jpeg", 0.82)); }
      catch { reject(new Error("Görsel işlenemedi.")); }
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Görsel açılamadı — dosya bozuk olabilir.")); };
    img.src = url;
  });
}

export const photoKB = dataUrl => Math.round((dataUrl.length * 3) / 4 / 1024);

// Avatar + üzerine yerleşen yükleme düğmesi
export function photoBlock(person, kind, ring, size) {
  // Fotoğraf yükleme yetkisi yoksa yalnızca avatar gösterilir (§2).
  if (!yapabilir("fotografYukle")) return avatarMarkup(person, ring, size);

  return `
  <div class="relative shrink-0">
    ${avatarMarkup(person, ring, size)}
    <button data-photo-pick="${kind}:${person.id}" aria-label="${person.photo ? "Fotoğrafı değiştir" : "Fotoğraf yükle"}"
      title="${person.photo ? "Fotoğrafı değiştir" : "Fotoğraf yükle"}"
      class="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-ink text-white grid place-items-center shadow-card hover:bg-inksoft transition">
      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M3 9a2 2 0 012-2h1.5l1-1.6A1 1 0 019.3 5h5.4a1 1 0 01.8.4l1 1.6H18a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
        <circle cx="12" cy="13" r="3"/>
      </svg>
    </button>
  </div>`;
}

// Taslak önizleme / rıza kutusu / kaldırma satırı
export function photoControls(person, kind) {
  if (!yapabilir("fotografYukle")) return "";
  if (state.photoError?.personId === person.id) {
    return `<div class="rounded-xl bg-coral-soft text-coral text-sm font-semibold px-3 py-2 mt-3">${esc(state.photoError.message)}</div>`;
  }

  const d = state.photoDraft?.personId === person.id ? state.photoDraft : null;
  const rizaGerekli = kind === "student";

  if (d) {
    return `
    <div class="rounded-xl border-2 border-dashed border-teal bg-teal-soft/50 p-4 mt-3">
      <div class="flex items-center gap-4">
        <img src="${esc(d.dataUrl)}" alt="Yüklenecek fotoğrafın önizlemesi" class="w-16 h-16 rounded-full object-cover shrink-0">
        <div class="min-w-0 flex-1">
          <div class="text-sm font-semibold">Fotoğraf yüklenmeye hazır</div>
          <div class="text-[11px] text-inksoft">${PHOTO_SIZE}×${PHOTO_SIZE} kare kırpıldı · ${photoKB(d.dataUrl)} KB</div>
        </div>
      </div>
      ${rizaGerekli ? `
        <label class="flex items-start gap-2 mt-3 text-xs cursor-pointer">
          <input type="checkbox" data-photo-consent ${d.consent ? "checked" : ""} class="mt-0.5 w-4 h-4 accent-teal shrink-0">
          <span>Velinin fotoğraf paylaşımına açık rızası alındı. Onay tarihi kayda işlenir.</span>
        </label>` : ""}
      <div class="flex items-center gap-2 mt-3">
        <button data-photo-save class="rounded-xl bg-teal text-white text-sm font-semibold px-4 py-2 disabled:opacity-40 disabled:cursor-not-allowed"
          ${rizaGerekli && !d.consent ? "disabled" : ""}>Kaydet</button>
        <button data-photo-cancel class="rounded-xl border border-line bg-white text-sm font-semibold px-4 py-2">Vazgeç</button>
      </div>
    </div>`;
  }

  if (person.photo) {
    return `
    <div class="flex items-center gap-2 mt-3 text-[11px] text-inksoft">
      <span>📷 Fotoğraf yüklü${person.photoConsentAt ? ` · veli onayı ${fmtDate(person.photoConsentAt)}` : ""}</span>
      <button data-photo-remove="${kind}:${person.id}" class="font-semibold text-coral hover:underline">Kaldır</button>
    </div>`;
  }
  return "";
}
